import { Box, Fade, Typography } from '@mui/material';

type Props = {
  seconds: number | null;
};

/**
 * JumpFeedbackOverlay displays the accumulated jump time in the center of the video player.
 * Shows "+Xs" or "-Xs" as users rapidly press J/L keys or click ±10s buttons.
 * Uses glassmorphic styling to match the app's visual language.
 */
/**
 * Formats seconds into a human-readable duration string.
 * Examples: 10 → "10s", 90 → "1m 30s", -150 → "2m 30s"
 */
const formatDuration = (totalSeconds: number): string => {
  const absSeconds = Math.abs(totalSeconds);

  if (absSeconds < 60) {
    return `${absSeconds}s`;
  }

  const minutes = Math.floor(absSeconds / 60);
  const remainingSeconds = absSeconds % 60;

  if (remainingSeconds === 0) {
    return `${minutes}m 00s`;
  }

  return `${minutes}m ${remainingSeconds}s`;
};

const JumpFeedbackOverlay = ({ seconds }: Props) => {
  const isVisible = seconds !== null;
  const sign = seconds !== null && seconds >= 0 ? '+' : '-';
  const label = seconds !== null ? `${sign}${formatDuration(seconds)}` : '';

  return (
    <Fade in={isVisible} timeout={{ enter: 150, exit: 50 }}>
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10,
          pointerEvents: 'none', // Don't block video clicks
          // Glassmorphic style matching app
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(6px)',

          textShadow: '1px 1px 5px #4f4f4f58',

          borderRadius: '8px',
          padding: '16px 32px',

        }}
      >
        <Typography
          variant="h4"
          sx={{
            color: 'white',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums', // Fixed-width numbers for stable display
          }}
        >
          {label}
        </Typography>
      </Box>
    </Fade>
  );
};

export default JumpFeedbackOverlay;
